import { Badge, Button } from 'reactstrap';
import classNames from 'classnames';
import { Card, CardBody } from '@g17eco/atoms/card/Card';
import { BaseDropdown, type BaseItem } from '@g17eco/molecules/dropdown';
import { ThemedImage } from '@features/theme/components/ThemedImage';
import './IntegrationProviderCard.scss';

interface ActionItem extends BaseItem {
  action: IntegrationProviderCardAction;
}

export type IntegrationCardStatus =
  | { kind: 'connected'; subtitle?: string; onAccess?: () => void }
  | { kind: 'ready'; onSetUp: () => void }
  | { kind: 'disabled' };

export interface IntegrationProviderCardAction {
  label: string;
  onClick: () => void;
}

interface Props {
  logo: string;
  /** Optional top-left brand icon. When provided, takes precedence over `logo` in the card header. */
  icon?: string;
  active?: boolean;
  name: string;
  description: string;
  learnMoreUrl?: string;
  status: IntegrationCardStatus;
  actions?: IntegrationProviderCardAction[];
  /** When set, rendered as a small "powered by" footer with the partner wordmark. */
  poweredByLogo?: string;
}

const StatusPill = ({ status }: { status: IntegrationCardStatus }) => {
  if (status.kind === 'connected') {
    return (
      <Badge pill color='success' className='integration-card-pill py-2'>
        <span className='integration-card-dot integration-card-dot-success' /> Connected
      </Badge>
    );
  }
  if (status.kind === 'ready') {
    return (
      <Badge pill color='info' className='outline integration-card-pill'>
        <span className='integration-card-dot integration-card-dot-info' /> Available
      </Badge>
    );
  }
  return null;
};

export const IntegrationProviderCard = ({
  logo,
  icon,
  active = false,
  name,
  description,
  learnMoreUrl,
  status,
  actions,
  poweredByLogo,
}: Props) => {
  const isDisabled = status.kind === 'disabled';
  const tileImage = icon ?? logo;

  return (
    <Card
      hover={!isDisabled}
      className={classNames('h-100 integration-card', isDisabled && 'integration-card-disabled', active && 'integration-card-active')}
    >
      <CardBody className='d-flex flex-column'>
        <div className='d-flex justify-content-between align-items-start mb-3'>
          <div className='integration-card-logo'>
            <ThemedImage src={tileImage} alt={`${name} logo`} />
          </div>
          <StatusPill status={status} />
        </div>

        <h5 className='mb-1'>{name}</h5>
        {status.kind === 'connected' && status.subtitle ? (
          <div className='text-ThemeTextLight text-xs mb-2'>{status.subtitle}</div>
        ) : null}
        <p className='text-ThemeTextMedium integration-card-description'>{description}</p>

        {poweredByLogo ? (
          <div className='integration-card-powered-by mt-2'>
            <span className='integration-card-powered-by-label'>powered by</span>
            <ThemedImage src={poweredByLogo} alt={`${name} powered-by logo`} />
          </div>
        ) : null}

        <div className='d-flex justify-content-between align-items-center mt-auto pt-3'>
          {status.kind === 'connected' ? (
            <>
              {actions && actions.length > 0 ? (
                <BaseDropdown<ActionItem>
                  items={actions.map((a) => ({ key: a.label, content: a.label, action: a }))}
                  toggleContent={
                    <span>
                      Manage <i className='fal fa-chevron-down ms-1' />
                    </span>
                  }
                  onClickItem={(item) => item.action.onClick()}
                  classes={{ dropdownToggle: 'btn btn-outline-secondary btn-sm' }}
                />
              ) : (
                <span />
              )}
              {status.onAccess ? (
                <Button color='primary' onClick={status.onAccess}>
                  Access <i className='fal fa-arrow-up-right ms-1' />
                </Button>
              ) : null}
            </>
          ) : null}

          {status.kind === 'ready' ? (
            <>
              {learnMoreUrl ? (
                <a
                  href={learnMoreUrl}
                  target='_blank'
                  rel='noreferrer'
                  className='text-ThemeAccentMedium'
                >
                  Learn more
                </a>
              ) : (
                <span />
              )}
              <Button color='primary' onClick={status.onSetUp}>
                Set up
              </Button>
            </>
          ) : null}

          {isDisabled && learnMoreUrl ? (
            <a
              href={learnMoreUrl}
              target='_blank'
              rel='noreferrer'
              className='text-ThemeTextLight'
            >
              Learn more
            </a>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
};
